/**
 * Threads in the data store (2026-10-29): a thread is a channel
 * (`DataState.channels`, type 11 or 12, `parent_id` its text channel), so
 * history, unread marks, typing and pins work by its ID as for any channel.
 * This file applies the thread events and keeps each thread's `member` (the
 * signed-in user's own membership), which shared payloads never carry.
 */
import {
  compareSnowflakes,
  isThreadChannelType,
  type Channel,
  type GatewayDispatch,
  type Snowflake,
  type ThreadMember,
} from '@neosocial/protocol';
import type { DataState } from '../store/reducer';

type ThreadFrame = Extract<
  GatewayDispatch,
  { t: 'THREAD_CREATE' | 'THREAD_UPDATE' | 'THREAD_DELETE' | 'THREAD_MEMBERS_UPDATE' }
>;

/** Puts a thread in the store; a payload without `member` keeps the one held. */
export function putThread(s: DataState, c: Channel): DataState {
  const prev = s.channels[c.id];
  const next: Channel = { ...prev, ...c };
  return { ...s, channels: { ...s.channels, [c.id]: next } };
}

/** Puts a thread list (REST `threads` + the caller's `members`) in the store. */
export function putThreads(
  s: DataState,
  list: readonly Channel[],
  members: readonly ThreadMember[] = [],
): DataState {
  if (!list.length) return s;
  const mine = new Map(members.map((m) => [m.id ?? '', m]));
  const channels = { ...s.channels };
  for (const c of list) {
    const m = mine.get(c.id);
    channels[c.id] = { ...channels[c.id], ...c, ...(m ? { member: m } : {}) };
  }
  return { ...s, channels };
}

/** Forgets a thread and its loaded history. */
export function dropThread(s: DataState, id: Snowflake): DataState {
  if (!s.channels[id] && !s.messages[id]) return s;
  const channels = { ...s.channels };
  delete channels[id];
  const messages = { ...s.messages };
  delete messages[id];
  return { ...s, channels, messages };
}

/** The thread events (THREAD_CREATE, _UPDATE, _DELETE, _MEMBERS_UPDATE). */
export function applyThreadDispatch(s: DataState, frame: ThreadFrame): DataState {
  switch (frame.t) {
    case 'THREAD_CREATE':
    case 'THREAD_UPDATE': {
      const { newly_created: _new, ...c } = frame.d as Channel & { newly_created?: boolean };
      return putThread(s, c);
    }
    case 'THREAD_DELETE':
      return dropThread(s, frame.d.id);
    case 'THREAD_MEMBERS_UPDATE': {
      const d = frame.d;
      const ch = s.channels[d.id];
      if (!ch) return s;
      const me = s.me?.id;
      const next: Channel = { ...ch, member_count: d.member_count };
      const added = d.added_members?.find((x) => x.user_id === me);
      if (added) {
        const { member: _gm, ...own } = added;
        next.member = own;
      }
      if (me && d.removed_member_ids?.includes(me)) delete next.member;
      return { ...s, channels: { ...s.channels, [d.id]: next } };
    }
  }
}

/** A message arrived in a thread: one more (the server counts the same way). */
export function bumpThread(ch: Channel): Channel {
  return {
    ...ch,
    message_count: (ch.message_count ?? 0) + 1,
    total_message_sent: (ch.total_message_sent ?? 0) + 1,
  };
}

/** A message in a thread was deleted: one fewer (never below 0); any other channel is left as it is. */
export function threadCountDown(s: DataState, channelId: Snowflake): DataState {
  const th = s.channels[channelId];
  if (!th || !isThreadChannelType(th.type)) return s;
  const n = Math.max(0, (th.message_count ?? 0) - 1);
  return { ...s, channels: { ...s.channels, [channelId]: { ...th, message_count: n } } };
}

/** A parent channel went: its threads go with it. */
export function dropThreadsOf(
  channels: DataState['channels'],
  parentId: Snowflake,
): DataState['channels'] {
  let out: DataState['channels'] | null = null;
  for (const [id, c] of Object.entries(channels)) {
    if (c.parent_id !== parentId || !isThreadChannelType(c.type)) continue;
    out ??= { ...channels };
    delete out[id];
  }
  return out ?? channels;
}

/** True for a thread the signed-in user joined (its unread marks count; it shows in the sidebar). */
export function isJoinedThread(c: Channel | undefined): boolean {
  return !!c && isThreadChannelType(c.type) && !!c.member;
}

/** A channel's threads in the store, the most recent activity first. */
export function threadsOf(s: Pick<DataState, 'channels'>, parentId: Snowflake): Channel[] {
  return Object.values(s.channels)
    .filter((c) => c.parent_id === parentId && isThreadChannelType(c.type))
    .sort((a, b) => compareSnowflakes(b.last_message_id ?? b.id, a.last_message_id ?? a.id));
}

/** The joined, active threads under a channel: the sidebar's rows (Discord). */
export function sidebarThreadsOf(s: Pick<DataState, 'channels'>, parentId: Snowflake): Channel[] {
  return threadsOf(s, parentId).filter((c) => !!c.member && !c.thread_metadata?.archived);
}
